import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/auth';
import { Check, X, RefreshCw, Eye, EyeOff, Settings, DollarSign, Wallet, ArrowRightLeft, Loader2 } from 'lucide-react';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';

interface WiseBalance {
  currency: string;
  amount: number;
}

interface WiseRate {
  rate: number;
  sourceCurrency: string;
  targetCurrency: string;
  sourceAmount: number;
  targetAmount: number;
  fee: number;
}

interface PaymentProvider {
  id: string;
  provider: string;
  isEnabled: boolean;
  settings: {
    minAmount?: number;
    maxAmount?: number;
    feePercent?: number;
    feeFixed?: number;
  } | null;
  createdAt: string;
  updatedAt: string;
}

const providerInfo: Record<string, { name: string; logo: string; color: string; bgColor: string; description: string }> = {
  paypal: {
    name: 'PayPal',
    logo: '/images/providers/paypal.png',
    color: '#003087',
    bgColor: '#ffffff',
    description: 'Pay affiliates directly to their PayPal accounts',
  },
  wise: {
    name: 'Wise',
    logo: '/images/providers/wise.svg',
    color: '#9FE870',
    bgColor: '#f0fbe8',
    description: 'International transfers with low fees',
  },
  stripe: {
    name: 'Stripe Connect',
    logo: '/images/providers/stripe.png',
    color: '#635BFF',
    bgColor: '#f5f5ff',
    description: 'Instant payouts to connected accounts',
  },
  binance: {
    name: 'Binance Pay',
    logo: '/images/providers/binance.png',
    color: '#F0B90B',
    bgColor: '#1E2329',
    description: 'Crypto payouts via Binance Pay ID',
  },
};

export default function PaymentSettings() {
  const { accessToken } = useAuthStore();
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [testResult, setTestResult] = useState<{ provider: string; success: boolean; message: string } | null>(null);

  // Wise specific states
  const [wiseBalances, setWiseBalances] = useState<WiseBalance[]>([]);
  const [wiseRate, setWiseRate] = useState<WiseRate | null>(null);
  const [loadingWiseBalances, setLoadingWiseBalances] = useState(false);
  const [loadingWiseRate, setLoadingWiseRate] = useState(false);
  const [wiseRateForm, setWiseRateForm] = useState({ source: 'USD', target: 'EUR', amount: '1000' });
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    clientId: '',
    clientSecret: '',
    profileId: '',
    sandbox: true,
    minAmount: 50,
    maxAmount: 10000,
    feePercent: 2,
    feeFixed: 0,
  });

  const API_BASE = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/providers`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });
      const data = await response.json();
      if (data.success) {
        setProviders(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigure = async (provider: string) => {
    try {
      // Build payload based on provider type
      const payload: Record<string, any> = {
        provider,
        sandbox: formData.sandbox,
        minAmount: formData.minAmount,
        maxAmount: formData.maxAmount,
        feePercent: formData.feePercent,
        feeFixed: formData.feeFixed,
      };

      if (provider === 'wise') {
        // Wise uses apiKey (stored in clientId field) and profileId
        payload.clientId = formData.clientId; // API Key
        payload.clientSecret = formData.profileId; // Profile ID (stored in clientSecret for backend compatibility)
        payload.profileId = formData.profileId;
      } else {
        payload.clientId = formData.clientId;
        payload.clientSecret = formData.clientSecret;
      }

      const response = await fetch(`${API_BASE}/api/v1/payment-providers/providers/configure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        setConfiguring(null);
        setFormData({
          clientId: '',
          clientSecret: '',
          profileId: '',
          sandbox: true,
          minAmount: 50,
          maxAmount: 10000,
          feePercent: 2,
          feeFixed: 0,
        });
        fetchProviders();
      } else {
        alert(data.error || 'Failed to configure provider');
      }
    } catch (error) {
      console.error('Failed to configure provider:', error);
      alert('Failed to configure provider');
    }
  };

  const handleToggle = async (provider: string, enabled: boolean) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/providers/${provider}/toggle`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
        body: JSON.stringify({ enabled }),
      });

      const data = await response.json();
      if (data.success) {
        fetchProviders();
      } else {
        alert(data.error || 'Failed to toggle provider');
      }
    } catch (error) {
      console.error('Failed to toggle provider:', error);
    }
  };

  const handleTest = async (provider: string) => {
    setTesting(provider);
    setTestResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/providers/${provider}/test`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });

      const data = await response.json();
      setTestResult({
        provider,
        success: data.success,
        message: data.message || data.error || 'Unknown result',
      });
    } catch (error: any) {
      setTestResult({
        provider,
        success: false,
        message: error.message || 'Connection test failed',
      });
    } finally {
      setTesting(null);
    }
  };

  const getProviderConfig = (providerName: string) => {
    return providers.find((p) => p.provider === providerName);
  };

  // Wise specific functions
  const fetchWiseBalances = async () => {
    setLoadingWiseBalances(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/providers/wise/balances`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Tenant-ID': 'demo-casino',
        },
      });
      const data = await response.json();
      if (data.success) {
        setWiseBalances(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch Wise balances:', error);
    } finally {
      setLoadingWiseBalances(false);
    }
  };

  const fetchWiseRate = async () => {
    setLoadingWiseRate(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/payment-providers/providers/wise/rate?source=${wiseRateForm.source}&target=${wiseRateForm.target}&amount=${wiseRateForm.amount}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'X-Tenant-ID': 'demo-casino',
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setWiseRate(data.data);
      } else {
        alert(data.error || 'Failed to get exchange rate');
      }
    } catch (error) {
      console.error('Failed to fetch Wise rate:', error);
    } finally {
      setLoadingWiseRate(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const toggleProviderExpand = (provider: string) => {
    if (expandedProvider === provider) {
      setExpandedProvider(null);
    } else {
      setExpandedProvider(provider);
      if (provider === 'wise') {
        fetchWiseBalances();
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Payment Providers</h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Configure payment methods for affiliate payouts
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
          <DollarSign className="w-4 h-4" />
          <span>{providers.filter(p => p.isEnabled).length} active providers</span>
        </div>
      </div>

      {/* Provider List */}
      <div className="space-y-4">
        {Object.entries(providerInfo).map(([key, info]) => {
          const config = getProviderConfig(key);
          const isConfigured = !!config;
          const isEnabled = config?.isEnabled ?? false;

          return (
            <div
              key={key}
              className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden"
            >
              {/* Provider Row */}
              <div className="p-5">
                <div className="flex items-center justify-between">
                  {/* Left: Provider Info */}
                  <div className="flex items-center gap-4">
                    <div
                      className="w-12 h-12 rounded-md flex items-center justify-center overflow-hidden"
                      style={{ backgroundColor: info.bgColor }}
                    >
                      <img
                        src={info.logo}
                        alt={info.name}
                        className="w-8 h-8 object-contain"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-base font-medium text-[var(--text-primary)]">{info.name}</h3>
                        <Badge
                          variant={isConfigured ? (isEnabled ? 'success' : 'warning') : 'secondary'}
                        >
                          {isConfigured ? (isEnabled ? 'Active' : 'Disabled') : 'Not Configured'}
                        </Badge>
                      </div>
                      <p className="text-sm text-[var(--text-muted)] mt-0.5">{info.description}</p>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-2">
                    {isConfigured ? (
                      <>
                        <button
                          onClick={() => handleTest(key)}
                          disabled={testing === key}
                          className="px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded transition-colors disabled:opacity-50"
                        >
                          {testing === key ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            'Test Connection'
                          )}
                        </button>
                        <button
                          onClick={() => setConfiguring(key)}
                          className="px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded transition-colors flex items-center gap-1.5"
                        >
                          <Settings className="w-4 h-4" />
                          Configure
                        </button>
                        <Button
                          variant={isEnabled ? 'danger' : 'success'}
                          size="sm"
                          onClick={() => handleToggle(key, !isEnabled)}
                        >
                          {isEnabled ? 'Disable' : 'Enable'}
                        </Button>
                      </>
                    ) : (
                      <Button variant="primary" size="sm" onClick={() => setConfiguring(key)}>
                        Set Up Provider
                      </Button>
                    )}
                  </div>
                </div>

                {/* Settings Summary - Only show when configured */}
                {isConfigured && config.settings && (
                  <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-8 text-sm">
                        <div>
                          <span className="text-[var(--text-muted)]">Min Payout:</span>
                          <span className="ml-2 text-[var(--text-primary)] font-medium">
                            ${config.settings.minAmount || 50}
                          </span>
                        </div>
                        <div>
                          <span className="text-[var(--text-muted)]">Max Payout:</span>
                          <span className="ml-2 text-[var(--text-primary)] font-medium">
                            ${config.settings.maxAmount?.toLocaleString() || '10,000'}
                          </span>
                        </div>
                        <div>
                          <span className="text-[var(--text-muted)]">Fee:</span>
                          <span className="ml-2 text-[var(--text-primary)] font-medium">
                            {config.settings.feePercent || 0}%
                            {config.settings.feeFixed ? ` + $${config.settings.feeFixed}` : ''}
                          </span>
                        </div>
                      </div>
                      {/* Expand button for Wise */}
                      {key === 'wise' && isEnabled && (
                        <button
                          onClick={() => toggleProviderExpand(key)}
                          className="px-3 py-1.5 text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1.5"
                        >
                          <Wallet className="w-4 h-4" />
                          {expandedProvider === key ? 'Hide Details' : 'View Balance & Rates'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Wise Expanded Panel */}
                {key === 'wise' && expandedProvider === 'wise' && isConfigured && isEnabled && (
                  <div className="mt-4 pt-4 border-t border-[var(--border-color)] space-y-4">
                    {/* Balances Section */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-2">
                          <Wallet className="w-4 h-4" />
                          Account Balances
                        </h4>
                        <button
                          onClick={fetchWiseBalances}
                          disabled={loadingWiseBalances}
                          className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] flex items-center gap-1"
                        >
                          <RefreshCw className={`w-3 h-3 ${loadingWiseBalances ? 'animate-spin' : ''}`} />
                          Refresh
                        </button>
                      </div>
                      {loadingWiseBalances ? (
                        <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading balances...
                        </div>
                      ) : wiseBalances.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {wiseBalances.map((balance) => (
                            <div
                              key={balance.currency}
                              className="bg-[var(--bg-tertiary)] rounded-lg p-3"
                            >
                              <div className="text-xs text-[var(--text-muted)]">{balance.currency}</div>
                              <div className="text-lg font-semibold text-[var(--text-primary)]">
                                {formatCurrency(balance.amount, balance.currency)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-[var(--text-muted)]">No balances available</div>
                      )}
                    </div>

                    {/* Exchange Rate Calculator */}
                    <div>
                      <h4 className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-2 mb-3">
                        <ArrowRightLeft className="w-4 h-4" />
                        Exchange Rate Calculator
                      </h4>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="block text-xs text-[var(--text-muted)] mb-1">Amount</label>
                          <input
                            type="number"
                            value={wiseRateForm.amount}
                            onChange={(e) => setWiseRateForm({ ...wiseRateForm, amount: e.target.value })}
                            className="w-28 px-3 py-1.5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-sm text-[var(--text-primary)]"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[var(--text-muted)] mb-1">From</label>
                          <select
                            value={wiseRateForm.source}
                            onChange={(e) => setWiseRateForm({ ...wiseRateForm, source: e.target.value })}
                            className="w-20 px-2 py-1.5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-sm text-[var(--text-primary)]"
                          >
                            {['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'MXN', 'BRL'].map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-[var(--text-muted)] mb-1">To</label>
                          <select
                            value={wiseRateForm.target}
                            onChange={(e) => setWiseRateForm({ ...wiseRateForm, target: e.target.value })}
                            className="w-20 px-2 py-1.5 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-sm text-[var(--text-primary)]"
                          >
                            {['EUR', 'USD', 'GBP', 'CAD', 'AUD', 'MXN', 'BRL'].map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={fetchWiseRate}
                          disabled={loadingWiseRate}
                        >
                          {loadingWiseRate ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Get Rate'
                          )}
                        </Button>
                      </div>

                      {/* Rate Result */}
                      {wiseRate && (
                        <div className="mt-3 p-3 bg-[var(--bg-tertiary)] rounded-lg">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                            <div>
                              <div className="text-xs text-[var(--text-muted)]">Exchange Rate</div>
                              <div className="font-semibold text-[var(--text-primary)]">
                                1 {wiseRate.sourceCurrency} = {wiseRate.rate.toFixed(4)} {wiseRate.targetCurrency}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-[var(--text-muted)]">You Send</div>
                              <div className="font-semibold text-[var(--text-primary)]">
                                {formatCurrency(wiseRate.sourceAmount, wiseRate.sourceCurrency)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-[var(--text-muted)]">They Receive</div>
                              <div className="font-semibold text-green-400">
                                {formatCurrency(wiseRate.targetAmount, wiseRate.targetCurrency)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-[var(--text-muted)]">Wise Fee</div>
                              <div className="font-semibold text-yellow-400">
                                {formatCurrency(wiseRate.fee, wiseRate.sourceCurrency)}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Test Result */}
                {testResult?.provider === key && (
                  <div
                    className={`mt-4 p-3 rounded flex items-center gap-2 text-sm ${
                      testResult.success
                        ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                        : 'bg-red-500/10 text-red-400 border border-red-500/20'
                    }`}
                  >
                    {testResult.success ? (
                      <Check className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span>{testResult.message}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Configuration Modal */}
      {configuring && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setConfiguring(null)}
          />
          <div className="relative bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg shadow-2xl w-full max-w-lg mx-4">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-[var(--border-color)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center overflow-hidden"
                  style={{ backgroundColor: providerInfo[configuring]?.bgColor }}
                >
                  <img
                    src={providerInfo[configuring]?.logo}
                    alt={providerInfo[configuring]?.name}
                    className="w-7 h-7 object-contain"
                  />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                    Configure {providerInfo[configuring]?.name}
                  </h2>
                  <p className="text-sm text-[var(--text-muted)]">Enter your API credentials</p>
                </div>
              </div>
              <button
                onClick={() => setConfiguring(null)}
                className="p-1 hover:bg-[var(--bg-tertiary)] rounded transition-colors"
              >
                <X className="w-5 h-5 text-[var(--text-muted)]" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* API Credentials Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                  API Credentials
                </h3>

                {configuring === 'wise' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                        API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showSecret[configuring] ? 'text' : 'password'}
                          className="w-full px-3 py-2 pr-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                          value={formData.clientId}
                          onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                          placeholder="Enter Wise API key"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                          onClick={() =>
                            setShowSecret({ ...showSecret, [configuring]: !showSecret[configuring] })
                          }
                        >
                          {showSecret[configuring] ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-[var(--text-muted)]">
                        Get your API key from Wise Settings &gt; API tokens
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                        Profile ID
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                        value={formData.profileId}
                        onChange={(e) => setFormData({ ...formData, profileId: e.target.value })}
                        placeholder="Enter Wise Profile ID (numeric)"
                      />
                      <p className="mt-1 text-xs text-[var(--text-muted)]">
                        Your business or personal profile ID from Wise
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                        Client ID / API Key
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                        value={formData.clientId}
                        onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                        placeholder="Enter client ID"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                        Client Secret / API Secret
                      </label>
                      <div className="relative">
                        <input
                          type={showSecret[configuring] ? 'text' : 'password'}
                          className="w-full px-3 py-2 pr-10 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                          value={formData.clientSecret}
                          onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                          placeholder="Enter client secret"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                          onClick={() =>
                            setShowSecret({ ...showSecret, [configuring]: !showSecret[configuring] })
                          }
                        >
                          {showSecret[configuring] ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.sandbox}
                    onChange={(e) => setFormData({ ...formData, sandbox: e.target.checked })}
                    className="w-4 h-4 rounded border-[var(--border-color)] text-primary-500 focus:ring-primary-500/50"
                  />
                  <span className="text-sm text-[var(--text-secondary)]">
                    Sandbox / Test Mode
                  </span>
                </label>
              </div>

              {/* Payout Settings Section */}
              <div className="space-y-4 pt-4 border-t border-[var(--border-color)]">
                <h3 className="text-sm font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                  Payout Limits
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                      Minimum ($)
                    </label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                      value={formData.minAmount}
                      onChange={(e) => setFormData({ ...formData, minAmount: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                      Maximum ($)
                    </label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                      value={formData.maxAmount}
                      onChange={(e) => setFormData({ ...formData, maxAmount: Number(e.target.value) })}
                    />
                  </div>
                </div>
              </div>

              {/* Fee Settings Section */}
              <div className="space-y-4 pt-4 border-t border-[var(--border-color)]">
                <h3 className="text-sm font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                  Processing Fees
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                      Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                      value={formData.feePercent}
                      onChange={(e) => setFormData({ ...formData, feePercent: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                      Fixed Fee ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                      value={formData.feeFixed}
                      onChange={(e) => setFormData({ ...formData, feeFixed: Number(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-[var(--border-color)] flex justify-end gap-3 bg-[var(--bg-secondary)]">
              <Button variant="secondary" onClick={() => setConfiguring(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => handleConfigure(configuring)}
                disabled={
                  configuring === 'wise'
                    ? !formData.clientId || !formData.profileId
                    : !formData.clientId || !formData.clientSecret
                }
              >
                Save Configuration
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
